/**
 * frontend/src/pages/valuation/_OverviewBlockers.tsx
 *
 * "What this engagement still needs" — draft-sourced preflight blockers and
 * warnings, each one linked back to the tab that owns the field it names.
 * A blocker matching nothing gets no button.
 *
 * Moved out of ValuationOverview.tsx under VAL-OVERVIEW-1C §R0 (a file-size
 * split ahead of the R5-R7 blocks). Pure move — same JSX, same hooks, same
 * strings, same behaviour as VAL-OVERVIEW-1B. Takes the engagement id as a
 * prop; no logic change of any kind.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { preflightValuation } from '@/api/valuationB3'
import { useValuationDraft } from './_valuationDraft'
import { formatApiError } from '@/utils/httpErrors'
import { useAuth } from '@/contexts/AuthContext'
import { nextStep } from './_nextStep'
import { stepsFor } from './_steps'
import type { PreflightResponse } from '@/types/valuationB3'
import type { ValuationEngagement } from '@/types/valuation'

// VAL-PARTICULARS-UI-1 R4.2 — the ten VS Section 100 particulars fields
// (types/valuation.ts ValuationEngagement/UpdateEngagementBody) shown with
// their screen label, not the engine's field name. Nothing else is remapped.
const PARTICULARS_LABELS: Record<string, string> = {
  prepared_for: 'prepared for',
  purpose: 'purpose',
  intended_users: 'intended users',
  standard_of_value: 'standard of value',
  premise_of_value: 'premise of value',
  subject_interest: 'subject interest',
  interest_characteristics: 'interest characteristics',
  sources_relied_upon: 'sources relied upon',
  analyst_has_financial_interest: "analyst's financial interest disclosure",
  fee_is_contingent: 'contingent fee disclosure',
}

function withParticularsLabels(text: string): string {
  let out = text
  for (const [field, label] of Object.entries(PARTICULARS_LABELS)) {
    out = out.replace(new RegExp(`\\b${field}\\b`, 'g'), label)
  }
  return out
}

type OverviewTab = 'intake' | 'tuning' | 'company' | 'signoff'

// VAL-PATH-P7c: the signer's steps (Methodology and rates, Sign) take their
// name and route from _steps.ts; Intake and Company keep theirs.
const SIGNER_STEP_TABS: readonly OverviewTab[] = ['tuning', 'signoff']
const TAB_LABEL: Record<'intake' | 'company', string> = { intake: 'Intake', company: 'Company' }

function tabRoute(id: string, tab: OverviewTab): { path: string; label: string } {
  const step = SIGNER_STEP_TABS.includes(tab) ? stepsFor('valuation_signer').find(s => s.key === tab) : undefined
  if (step) return { path: step.route(id), label: step.label }
  const own = tab === 'company' ? 'company' : 'intake'
  return { path: `/valuation/${id}/${own}`, label: TAB_LABEL[own] }
}

// VAL-OVERVIEW-1B §BUILD — substring match, longest match wins. Keys are a
// mix of the engine's own field names and the natural-language phrasing
// those screens already use elsewhere (e.g. ValuationSignOff.tsx's
// REISSUE_FIELD_LABELS), grounded in which screen owns which field
// (ValuationTuning.tsx vs ValuationIntake.tsx — confirmed by grep, not
// guessed). A blocker matching nothing gets no button, per §BUILD.
const ROUTE_KEYWORDS: Array<{ match: string; tab: OverviewTab }> = [
  // Methodology / Tuning tab
  { match: 'benchmark_key', tab: 'tuning' },
  { match: 'benchmark', tab: 'tuning' },
  { match: 'normalized_earnings', tab: 'tuning' },
  { match: 'normalized earnings', tab: 'tuning' },
  { match: 'driver_answers', tab: 'tuning' },
  { match: 'driver questionnaire', tab: 'tuning' },
  { match: 'questionnaire', tab: 'tuning' },
  { match: 'driver', tab: 'tuning' },
  // VAL-INPUT-PERSIST-1D — the multiple override moved to Intake.
  { match: 'override_multiple', tab: 'intake' },
  { match: 'override_rationale', tab: 'intake' },
  { match: 'override_disposition', tab: 'intake' },
  { match: 'override multiple', tab: 'intake' },
  { match: 'override rationale', tab: 'intake' },
  { match: 'override disposition', tab: 'intake' },
  { match: 'override', tab: 'intake' },
  { match: 'naics_code', tab: 'tuning' },
  { match: 'naics', tab: 'tuning' },
  { match: 'sub_vertical', tab: 'tuning' },
  { match: 'sub-vertical', tab: 'tuning' },
  { match: 'sub vertical', tab: 'tuning' },
  { match: 'market_only_acknowledged', tab: 'tuning' },
  { match: 'market-only acknowledgement', tab: 'tuning' },
  { match: 'market only acknowledgement', tab: 'tuning' },
  { match: 'market-only', tab: 'tuning' },
  { match: 'market only', tab: 'tuning' },
  // VAL-INTAKE-BLANK-JUDGMENT-1 — the server's missing-rate blocker wording.
  { match: 'fill in on intake', tab: 'intake' },
  // Intake tab — financial inputs
  { match: 'total_assets', tab: 'intake' },
  { match: 'total assets', tab: 'intake' },
  { match: 'total_liabilities', tab: 'intake' },
  { match: 'total liabilities', tab: 'intake' },
  { match: 'fair_value_adjustments', tab: 'intake' },
  { match: 'fair value adjustments', tab: 'intake' },
  { match: 'fair-value adjustments', tab: 'intake' },
  // Company step (VAL-PATH-COMPANY P3) — particulars I1-I9 are edited on step 1
  { match: 'prepared_for', tab: 'company' },
  { match: 'prepared for', tab: 'company' },
  { match: 'purpose', tab: 'company' },
  { match: 'intended_users', tab: 'company' },
  { match: 'intended users', tab: 'company' },
  { match: 'standard_of_value', tab: 'company' },
  { match: 'standard of value', tab: 'company' },
  { match: 'premise_of_value', tab: 'company' },
  { match: 'premise of value', tab: 'company' },
  { match: 'subject_interest', tab: 'company' },
  { match: 'subject interest', tab: 'company' },
  { match: 'interest_characteristics', tab: 'company' },
  { match: 'interest characteristics', tab: 'company' },
  { match: 'sources_relied_upon', tab: 'company' },
  { match: 'sources relied upon', tab: 'company' },
  // Sign — the two declarations I10-I11 (VAL-PATH-P7c)
  { match: 'analyst_has_financial_interest', tab: 'signoff' },
  { match: 'financial interest', tab: 'signoff' },
  { match: 'fee_is_contingent', tab: 'signoff' },
  { match: 'contingent fee', tab: 'signoff' },
]

// VAL-JOURNEY-FIX-3: preflightError is formatApiError()'s full string — e.g.
// "400 - Bad Request | VALUATION_INPUT_INVALID | value-driver questionnaire
// is incomplete - 11 unanswered question(s)" (httpErrors.ts HttpErrorCatalog
// .format joins status/code/message with ' | ', and translateCode itself
// joins "CODE — description" with an em dash). The amber panel shows only
// the trailing human sentence, never the status/code prefix.
function humanPreflightSentence(raw: string): string {
  const parts = raw.split(' | ')
  const last = parts[parts.length - 1] ?? raw
  const dashIndex = last.indexOf(' — ')
  return dashIndex === -1 ? last : last.slice(dashIndex + 3)
}

function routeForBlocker(id: string, blocker: string): { path: string; label: string } | null {
  const lower = blocker.toLowerCase()
  let best: { tab: OverviewTab; matchLen: number } | null = null
  for (const { match, tab } of ROUTE_KEYWORDS) {
    if (lower.includes(match) && (!best || match.length > best.matchLen)) {
      best = { tab, matchLen: match.length }
    }
  }
  if (!best) return null
  return tabRoute(id, best.tab)
}

// VAL-SIGNED-STATE-1 R1: `liveVersion` is the caller's snapshot-history
// lookup, not this file's — optional because a signed engagement reads
// correctly with or without the number. WALKFIX-C AMEND_2: the number is no
// longer shown (owner 'Approve All' 2026-10-25 15:57); the argument stays for callers.
function signedOffLine(_liveVersion?: number | null): string {
  return 'Signed — nothing outstanding. Edit to start a new record.'
}

export function OverviewBlockers({
  id,
  engagement,
  liveVersion = null,
}: {
  id: string
  engagement: ValuationEngagement | null
  /** Live (status: 'final') snapshot version, from the Overview's own
   *  snapshot-history fetch. `null`/omitted renders the line without a
   *  number rather than guessing one. */
  liveVersion?: number | null
}) {
  const navigate = useNavigate()
  const { user } = useAuth()
  // R2: nextStep replaces this file's own hardcoded "Go to Intake" — falls
  // back to the old literal route/label while engagement is still loading.
  const step = engagement ? nextStep(engagement, user?.role) : null
  const isSignedOff = engagement?.status === 'signed_off'

  const { draft, draftTuning, loading: draftLoading } = useValuationDraft(id)
  const [preflight, setPreflight] = useState<PreflightResponse | null>(null)
  const [preflightError, setPreflightError] = useState<string | null>(null)
  const [preflightLoading, setPreflightLoading] = useState(false)

  // VAL-OVERVIEW-1B §THE ANSWER — draft === null means "no draft yet" (the
  // GET returns inputs: null, 200, never 404). Preflight must not be called
  // with a half body, so a null draft short-circuits before the POST.
  // VAL-SIGNED-STATE-1 R1: a signed engagement never runs the preflight —
  // there is nothing outstanding to preflight against.
  useEffect(() => {
    if (isSignedOff) return
    if (draftLoading) return
    if (draft === null) {
      setPreflight(null)
      setPreflightError(null)
      return
    }
    let live = true
    setPreflightLoading(true)
    preflightValuation(id)
      .then(res => { if (live) setPreflight(res) })
      .catch((err: unknown) => { if (live) setPreflightError(formatApiError(err)) })
      .finally(() => { if (live) setPreflightLoading(false) })
    return () => { live = false }
  }, [id, draft, draftTuning, draftLoading, isSignedOff])

  return isSignedOff ? (
    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
      <p className="text-sm font-medium text-green-800">{signedOffLine(liveVersion)}</p>
    </div>
  ) : draftLoading ? (
    <div className="space-y-2">
      <Skeleton className="h-4 w-56" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  ) : draft === null ? (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
      <p className="text-sm font-medium text-gray-900">Nothing recorded yet.</p>
      <p className="mt-1 text-sm text-gray-500">Start on Intake to see what this engagement needs.</p>
      <Button className="mt-3" size="sm" onClick={() => navigate(step?.route ?? `/valuation/${id}/intake`)}>
        {step?.label ?? 'Go to Intake'}
      </Button>
    </div>
  ) : preflightError ? (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
      <p className="text-sm font-medium text-amber-900">Before this can be signed:</p>
      <p className="mt-1 text-sm text-amber-800">{humanPreflightSentence(preflightError)}</p>
      <Button className="mt-3" size="sm" onClick={() => navigate(step?.route ?? `/valuation/${id}/intake`)}>
        {step?.label ?? 'Go to Intake'}
      </Button>
    </div>
  ) : preflightLoading && !preflight ? (
    <div className="space-y-2">
      <Skeleton className="h-4 w-56" />
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  ) : preflight ? (
    preflight.ok && preflight.blockers.length === 0 ? (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4">
        <p className="text-sm font-medium text-green-800">✓ Ready to sign.</p>
      </div>
    ) : (
      <div className="space-y-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            What this engagement still needs ({preflight.blockers.length})
          </h2>
          <div className="mt-2 space-y-2">
            {preflight.blockers.map((blocker, i) => {
              const route = routeForBlocker(id, blocker)
              return (
                <div
                  key={i}
                  className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 p-3"
                >
                  <span className="text-sm text-red-800">
                    <span className="mr-2">●</span>
                    {withParticularsLabels(blocker)}
                  </span>
                  {route && (
                    <Button size="sm" variant="outline" onClick={() => navigate(route.path)}>
                      {route.label}
                    </Button>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {preflight.warnings.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Worth checking</h3>
            <div className="mt-2 space-y-2">
              {preflight.warnings.map((warning, i) => {
                const route = routeForBlocker(id, warning)
                return (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3"
                  >
                    <span className="text-sm text-amber-800">
                      <span className="mr-2">●</span>
                      {withParticularsLabels(warning)}
                    </span>
                    {route && (
                      <Button size="sm" variant="outline" onClick={() => navigate(route.path)}>
                        {route.label}
                      </Button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  ) : null
}
